import { TabStyles } from '@tableau/widgets';
import * as React from 'react';

import { ServiceLocator } from '../../../app/ServiceLocator';
import { Formatters, Messages } from '../../../codegen/Localize';
import { classes, cssClass, flexColumnStyle, flexRowStyle } from '../../../style';
import { TextileHelper } from '../../../ts/helpers/TextileHelper';
import { imgPath } from '../../utilities/ImgUrl';
import { CommonStyles } from '../CommonStyles';

/**
 * This is a React refactor of the EditableTextarea
 * It uses the Textile text formatting which supports some markup, used currently for descriptions.
 */

const cssPrefix = 'editableDescription';

const rootClass = cssClass(cssPrefix, 'root', flexRowStyle);

const formattingHelpRoot = cssClass(cssPrefix, 'formattingHelp', flexRowStyle);

const formattingColumnLeft = cssClass(cssPrefix, 'formattingColumnLeft', flexColumnStyle, {
  width: 120,
  $nest: {
    'div': { padding: CommonStyles.Sizes.DefaultSpace }
  }
});

const formattingColumnRight = cssClass(cssPrefix, 'formattingColumn', flexColumnStyle, {
  flex: 1,
  $nest: {
    'div': { padding: CommonStyles.Sizes.DefaultSpace }
  }
});

const formattingHeader = cssClass(cssPrefix, 'formattingHeader', {
  padding: CommonStyles.Sizes.DefaultSpace,
  backgroundColor: CommonStyles.Colors.Grey230
});

const buttonContainer = cssClass(cssPrefix, 'buttonContainer', flexColumnStyle, {
  textAlign: 'center',
  marginLeft: CommonStyles.Sizes.LargeSpace,
  width: '135px'
});

const saveButtonDisabled: React.CSSProperties = {
  backgroundColor: TabStyles.Colors.GoDarkDisabled,
  borderColor: TabStyles.Colors.GoDarkDisabled,
  pointerEvents: 'none',
  userSelect: 'none',
  'msUserSelect': 'none',
  'MozUserSelect': 'none',
  'WebkitUserSelect': 'none'
};

const cancelButton = cssClass(cssPrefix, 'cancelButton', {
  marginTop: CommonStyles.Sizes.DefaultSpace
});

const staticText = cssClass(cssPrefix, 'staticText', {
  width: 500,
  boxSizing: 'border-box',
  padding: 7
});

const textAreaClass = cssClass(cssPrefix, 'textarea', {
  width: 500,
  boxSizing: 'border-box'
});

const imageHintIcon = cssClass(cssPrefix, 'imageHintIcon', {
  height: '19px',
  width: '19px',
  verticalAlign: 'middle'
});

const characterCounter = cssClass(cssPrefix, 'characterCounter', {
  float: 'right'
});

const overLimitCounter = cssClass(cssPrefix, 'overLimitCounter', {
  color: TabStyles.Colors.AttentionLight
});

const defaultMaxLength: number = 50000;

export type EditableDescriptionProps = {
  readonly originalText: string;
  readonly customMaxCharacterLength?: number;
  readonly startInEditMode?: boolean;
  readonly cancel?: () => void;
  readonly onSubmit: (text: string) => void;
};

export enum ViewType {
  view,
  preview,
  edit
}

export type EditableDescriptionState = {
  readonly textile: string;
  readonly mode: ViewType;
  readonly showHints: boolean;
};

export class EditableDescription extends React.PureComponent<EditableDescriptionProps, EditableDescriptionState> {

  private textAreaRef: HTMLTextAreaElement;
  private readonly sanitize: ng.sanitize.ISanitizeService;

  constructor(props: EditableDescriptionProps) {
    super(props);
    this.sanitize = ServiceLocator.get<ng.sanitize.ISanitizeService>('$sanitize');
    this.state = {
      textile: this.props.originalText ? this.props.originalText : '',
      mode: this.props.startInEditMode ?  ViewType.edit : ViewType.view,
      showHints: false
    };
  }

  private startEditing = () => {
    this.setState({ mode: ViewType.edit});
  }

  private startPreview = () => {
    this.setState({ textile: this.textAreaRef.value, mode: ViewType.preview});
  }

  private save = () => {
    this.setState({ textile: this.textAreaRef && this.textAreaRef.value ? this.textAreaRef.value : this.state.textile, mode: ViewType.view});
    this.props.onSubmit(this.state.textile);
  }

  private cancel = () => {
    this.props.cancel && this.props.cancel();
    this.setState({ mode: ViewType.view });
  }

  private toggleFormattingHints = () => {
    this.setState({ showHints: !this.state.showHints });
  }

  private updateTextile = () => {
    this.setState({ textile: this.textAreaRef.value});
  }

  private renderCharacterCounter(): JSX.Element {
    return (
      <div className={classes(characterCounter, this.state.textile.length > (this.props.customMaxCharacterLength || defaultMaxLength) && overLimitCounter)}>
        {Messages.characterCounter({COUNT_LOC: Formatters.formatCount(this.state.textile  ? this.state.textile.length : 0), MAXIMUM_LOC: Formatters.formatCount(this.props.customMaxCharacterLength || defaultMaxLength)})}
      </div>
    );
  }

  private renderEditAndPreviewButtons(): JSX.Element {
    return this.state.mode === ViewType.view || this.state.mode === ViewType.preview ?
      <span onClick={this.startEditing} tb-test-id="btnEdit" className={'tb-outline-button ng-scope'}>{Messages.editDescription()}</span>
    : <span onClick={this.startPreview} tb-test-id="btnPreview" className={'tb-outline-button ng-scope'}>{Messages.preview()}</span>;
  }

  private renderSaveAndCancelButtons(): JSX.Element {
    const saveButtonStyle = this.state.textile.length > (this.props.customMaxCharacterLength || defaultMaxLength) ? saveButtonDisabled : null;

    return this.state.mode === ViewType.edit || this.state.mode === ViewType.preview ?
      <React.Fragment>
        <span onClick={this.save} tb-test-id="btnSave" className={classes('tb-outline-button', 'tb-go-button', 'tb-margin-top-wide')} style={saveButtonStyle}>{Messages.save()}</span>
        <span onClick={this.cancel} tb-test-id="btnCancel" className={classes(cancelButton, 'tb-outline-button', 'tb-cancel-button')}>{Messages.cancel()}</span>
      </React.Fragment> : null;
  }

  private renderButtons(): JSX.Element {
    return (
      <div className={classes(buttonContainer, 'tb-editable-textarea-buttons')}>
        {this.renderEditAndPreviewButtons()}
        {this.renderSaveAndCancelButtons()}
      </div>
    );
  }

  private renderHintsMessages(): JSX.Element {
    return (
      <div className={classes(formattingHelpRoot, 'tb-formatting-hints-table')}>
        <div className={formattingColumnLeft}>
          <div className={formattingHeader}>{Messages.formattingHintsColumnExample()}</div>
          <div><a>{Messages.formattingHintsLink()}</a></div>
          <div><strong>{Messages.formattingHintsBold()}</strong></div>
          <div><em>{Messages.formattingHintsItalics()}</em></div>
          <div><ins>{Messages.formattingHintsUnderline()}</ins></div>
          <div><img src={imgPath('image_hint_icon.svg')} className={imageHintIcon}/>{Messages.formattingHintsImage()}</div>
        </div>
        <div className={formattingColumnRight}>
          <div className={formattingHeader}>{Messages.formattingHintsColumnMarkup()}</div>
          <div>{`"${Messages.formattingHintsLink()}":http://www.example.com`}</div>
          <div>{`*${Messages.formattingHintsBold()}*`}</div>
          <div>{`_${Messages.formattingHintsItalics()}_`}</div>
          <div>{`+${Messages.formattingHintsUnderline()}+`}</div>
          <div>{'!http://www.example.com/image.png!'}</div>
        </div>
      </div>
    );
  }

  private renderFormattingHints(): JSX.Element {
    return (
      <div className={'tb-editable-textarea-bottom'}>
        <div className={'tb-formatting-hints ng-isolate-scope'} tb-test-id="formatting-hints-editable-textarea">
          <div className={'tb-formatting-hints-buttons'}>
            <a onClick={this.toggleFormattingHints} className={this.state.showHints ? 'tb-hide-formatting-hints' : 'tb-show-formatting-hints'}>
              {this.state.showHints ? Messages.hideFormattingHints() : Messages.showFormattingHints()}
            </a>
            {this.renderCharacterCounter()}
          </div>
          {this.state.showHints ? this.renderHintsMessages() : null}
        </div>
      </div>
    );
  }

  private setupTextareaRef = (textarea: HTMLTextAreaElement) => this.textAreaRef = textarea;

  private renderEditView(): JSX.Element {
    return (
      <div className={'tb-editable-textarea'}>
        <textarea onChange={this.updateTextile} ref={this.setupTextareaRef} className={classes(textAreaClass, 'tb-textile-editor-textarea')}>{this.state.textile}</textarea>
        {this.renderFormattingHints()}
      </div>
    );
  }

  private renderDescription(): JSX.Element {
    return this.state.mode === ViewType.view || this.state.mode === ViewType.preview ?
      <div className={classes(staticText, 'tb-textile', 'tb-enable-selection')} dangerouslySetInnerHTML={{__html: TextileHelper.getSanitizedHtmlFromTextile(this.state.textile, this.sanitize)}}/>
      : this.renderEditView();
  }

  public render(): JSX.Element {
    return (
      <div className={rootClass}>
        <div className={'tb-details-value'}>
          {this.renderDescription()}
        </div>
        <div className={'tb-about-actions tb-details-button-cell'}>
          {/* TODO: if is not read only*/ this.renderButtons()}
        </div>
      </div>
    );
  }
}
